import type { LocalMedication } from "@louise/care/treatment-phases";

/**
 * Which medications belong on a step's tab.
 *
 * Medications are linked to an API phase, not to a hub step, and several steps
 * share one phase — `STEP_TO_PHASE_TYPE` in `usePhases.ts` maps `protocol`,
 * `stimulation` and `trigger_shot` all onto the single `protocol` phase. So
 * matching on the phase alone gives every step that shares it the same list.
 *
 * The egg-freezing flow is the one that separates the injection from the
 * stimulation it ends: `pre_treatment → stimulation → trigger_shot →
 * egg_retrieval → freezing`. There, the whole stimulation protocol turned up on
 * the Trigger tab — Cetrotide, a GnRH antagonist that suppresses the surge,
 * listed under copy about "36 hours after this injection". Clinically wrong,
 * and wrong in the direction that misinforms.
 *
 * Within the shared phase the medication's role is what tells them apart. A
 * null role is *not* a trigger: the API marks the ones it recognises (an hCG
 * molecule, or the trigger bit on the medication type), and treating an
 * unmarked drug as one is exactly the error being fixed.
 */
/** The one step whose every medication is, by definition, a trigger. */
export const TRIGGER_STEP_ID = "trigger_shot";

/**
 * Only the two fields the rule reads, so callers keep their own row type.
 *
 * Both are optional on `LocalMedication` and stay optional here: an absent role
 * and a null one mean the same thing to this rule — not a recognised trigger —
 * and requiring them would stop a real medication row satisfying the type.
 */
type PhaseLinkedMedication = Partial<
  Pick<LocalMedication, "treatmentStepId" | "medicationRole">
>;

export function selectStepMedications<T extends PhaseLinkedMedication>(args: {
  /** The hub step whose tab is being filled. */
  stepId: string;
  /** The API phase this step reads and writes, when the cycle has one. */
  stepPhaseId: string | null | undefined;
  /**
   * The phase the flow's `trigger_shot` step uses, when the flow has one.
   * Null for every flow that keeps the injection on the step owning the phase —
   * ivf, icsi, iui, fet, oi, embryo freezing — which therefore splits nothing.
   */
  triggerPhaseId: string | null | undefined;
  medications: readonly T[];
}): T[] {
  const { stepId, stepPhaseId, triggerPhaseId, medications } = args;
  if (!stepPhaseId) return [];

  const onThisPhase = medications.filter(
    (med) => !!med.treatmentStepId && med.treatmentStepId === stepPhaseId,
  );

  // Nothing to separate unless the trigger step is sharing *this* phase. A
  // trigger-flagged medication logged on some other phase of the same cycle
  // stays where the patient put it.
  if (!triggerPhaseId || triggerPhaseId !== stepPhaseId) return onThisPhase;

  const isTrigger = (med: T) => med.medicationRole === "trigger_shot";
  return stepId === TRIGGER_STEP_ID
    ? onThisPhase.filter(isTrigger)
    : onThisPhase.filter((med) => !isTrigger(med));
}

/**
 * The phase a flow's trigger step shares, read off the same step progress the
 * caller already resolved. Returns null when the flow has no separate trigger
 * step, which is the signal `selectStepMedications` uses to split nothing.
 */
export function resolveTriggerStepPhaseId(
  stepProgress:
    readonly { stepId: string; phaseId?: string | null }[] | null | undefined,
): string | null {
  return (
    stepProgress?.find((step) => step.stepId === TRIGGER_STEP_ID)?.phaseId ??
    null
  );
}

/**
 * The recorded role, when it says something the catalogue cannot.
 *
 * Only `"trigger_shot"` is authoritative, and the asymmetry is the point. This
 * value exists to *add* what the catalogue does not know — she told us this is
 * her trigger — never to withhold what it does. `"standard"` is not an answer:
 * it is the default every creation outside a trigger context writes, so
 * treating it as one would let it short-circuit the catalogue and hide a real
 * trigger. An hCG shot logged from the Stimulation tab is stored `"standard"`,
 * and it is still a trigger.
 *
 * Anything else falls through too: the API types this column as a bare
 * `string`, and a role this app cannot interpret is not a claim it should
 * repeat.
 */
export function recordedMedicationRole(
  value: string | null | undefined,
): LocalMedication["medicationRole"] {
  return value === "trigger_shot" ? value : null;
}
